//* Importing necessary modules
import { useQuery } from "@tanstack/react-query";

//* Importing API
import { featureAPI } from "./featureAPI";

//* Importing types
import { type Feature, type FeatureResponse } from "./types/types";

/**
 * Custom hook to fetch and manage features data.
 *
 * @param {string} token - The authentication token required to access the API.
 * @returns {Object} An object containing the following properties:
 * - `features` {Feature[]} - An array of features fetched from the API.
 * - `loading` {boolean} - A boolean indicating if the data is currently being loaded or fetched.
 * - `error` {string | null} - An error message if an error occurred, otherwise null.
 * - `refetch` {Function} - A function to manually refetch the data.
 */
export const useFeatures = (token: string) => {
  const queryKey = ["features"];

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<
    FeatureResponse,
    Error
  >({
    queryKey,
    queryFn: () => featureAPI.getAll(token),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // Processa as features retornadas pela API
  const featuresData = data?.data.features || {};

  // Converte o objeto de features em um array
  const features: Feature[] = Object.values(featuresData);

  return {
    features,
    loading: isLoading || isFetching,
    error: isError ? error.message : null,
    refetch,
  };
};
